import type { ReactNode } from "react";

import { GitInfo } from "~/components/GitInfo";
import { GitDetails } from "~/types";

export function inputType(argType: string | string[]): string {
  switch (argType) {
    case "string":
      return "text";
    case "string_array":
      return "text";
    case "array":
      return "text";
    case "object":
      return "textarea";
    case "sha":
      return "text";
    case "int":
      return "number";
    case "bool":
      return "checkbox";
    default:
      console.warn(
        `Unknown argument type: ${argType}, defaulting to text input`,
      );
      return "text";
  }
}

// Object.entries with bool args last, so autofocus lands on a typeable
// field rather than a checkbox.
export function sortedArgumentEntries(
  args: Record<string, any>,
): [string, any][] {
  return Object.entries(args).sort(
    (a, b) => Number(a[1]?.type === "bool") - Number(b[1]?.type === "bool"),
  );
}

export const operationNotes = new Map<
  string,
  {
    note?: string;
    validations?: Record<string, (value: string) => string | null>;
  }
>([
  [
    "restart",
    {
      note: "This operation will restart all pods matching a specific prefix by deleting the pods and letting the StatefulSet controller recreate them. WARNING: This will cause downtime if used on query nodes.",
    },
  ],
  [
    "upgrade",
    {
      note: "Enter the Git SHA to upgrade to. This will trigger a rolling upgrade of all turbopuffer pods in the cluster, including query (plain, TQD, and pinned), index (plain and weighted), billing, and router pods.",
      validations: {
        target_version: (value: string) => {
          if (!/^[a-f0-9]{40}$/.test(value)) {
            return "target_version must be a 40-character Git SHA";
          }
          return null;
        },
      },
    },
  ],
  [
    "upgrade_latest",
    {
      note: "This operation will automatically upgrade to the latest available Git SHA that passes checks. This will trigger a rolling upgrade of all turbopuffer pods in the cluster.",
    },
  ],
  [
    "scale",
    {
      validations: {
        role: (value: string) => {
          if (!["Query", "Index", "Router", "Billing"].includes(value)) {
            return "role must be 'Query', 'Index', 'Router', or 'Billing'";
          }
          return null;
        },
        replicas: (value: string) => {
          if (parseInt(value) < 0) {
            return "replicas must be a positive integer";
          }
          return null;
        },
        // The deployment table serializes every target's desired replicas
        // into this field; absent when scaling a role.
        query_deployment: (value: string) => {
          if (!value) {
            return null;
          }
          const targets: Record<string, unknown> = JSON.parse(value).targets;
          for (const [target, replicas] of Object.entries(targets)) {
            if (!Number.isInteger(replicas) || (replicas as number) < 0) {
              return `${target} replicas must be a non-negative integer`;
            }
          }
          return null;
        },
      },
    },
  ],
  [
    "get_config",
    {
      note: "Optionally specify a role to get configuration for. Role must be 'query', 'index', 'router', or 'billing'. Leave empty to get the default configuration plus the 'tidy' role's config.",
      validations: {
        role: (value: string) => {
          if (
            value &&
            !["query", "index", "router", "billing"].includes(value)
          ) {
            return "role must be 'query', 'index', 'router', or 'billing'";
          }
          return null;
        },
      },
    },
  ],
  [
    "tidy",
    {
      note: "Optionally pin the job to one or more turbopuffer-role node labels, comma-separated. Allowed values: 'index', 'maintenance', or 'index-N' (e.g. 'index-64'). Leave empty to run on the default maintenance/index pool.",
    },
  ],
  [
    "set_config",
    {
      note: 'Set configuration values in the specified ConfigMap. The data field must be a valid JSON object of key-value pairs (e.g., {"cache.prefer_uring": "true", "some.key": "value"}).',
      validations: {
        data: (value: string) => {
          try {
            const parsed = JSON.parse(value);
            if (typeof parsed !== "object" || Array.isArray(parsed)) {
              return "data must be a JSON object";
            }
            // Validate all values are strings
            for (const [key, val] of Object.entries(parsed)) {
              if (typeof val !== "string") {
                return `all values must be strings, but "${key}" has type ${typeof val}`;
              }
            }
            return null;
          } catch (_) {
            return "data must be valid JSON";
          }
        },
      },
    },
  ],
]);
export function ShaDetails({
  gitDetails,
  footer,
  className,
  isByoc = false,
  onRefresh,
  isRefreshing,
}: {
  gitDetails: GitDetails;
  footer?: ReactNode;
  className?: string;
  isByoc?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  return (
    <div className={className}>
      <p className="mb-2 text-sm font-medium text-gray-900">
        turbopuffer SHA details:
      </p>
      <GitInfo
        gitDetails={gitDetails}
        footer={footer}
        showDetails={true}
        isByoc={isByoc}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />
    </div>
  );
}
